import * as React from 'react';
import Deduction from "../../../model/Deduction";
import {FormLabel, Grid, Typography, useTheme} from "@mui/material";
import {useEffect, useState} from "react";
import {
  dateToLocalFormat,
  numberToMoneyFormat,
  numberToPercentFormat
} from "../../../stores/StoreUtilities";
import Model from "../../../model/Model";
import {DeductionExpenseIcon} from "../../../styles/Icons";
import ModelCard2 from "../../../components/model/ModelCard2";
import Asset from "../../../model/Asset";
import DeductionEditDialog from "./DeductionEditDialog";
import {isNumber} from "../../../model/ModelUtilities";
import Snapshot from "../../../model/Snapshot";

const DeductionCard = ({
  deduction,
  model,
  snapshot,
  projected,
  expanded,
  onChange,
  onSave,
  onDelete,
  onArchive,
  dragHandleProps
}: {
  deduction: Deduction
  model: Model
  snapshot?: Snapshot
  projected?: number
  expanded?: boolean
  onChange?(event: any, expanded: boolean): void
  onSave?(deduction: Deduction): void
  onDelete?(deduction: Deduction): void
  onArchive?(deduction: Deduction): void
  dragHandleProps?: any
}) => {
  const [deductionModel, setDeductionModel] = useState<Deduction>(deduction)
  const [label, setLabel] = useState<string | undefined>()
  const [description, setDescription] = useState<string>("")
  const [amount, setAmount] = useState<string | undefined>()
  const [amountDate, setAmountDate] = useState<string | undefined>()
  const [projectedAmount, setProjectedAmount] = useState<string | undefined>()
  const [owner, setOwner] = useState<string | undefined>()
  const [asset, setAsset] = useState<Asset | undefined>()
  const [schedule, setSchedule] = useState<string | undefined>()
  const [startDate, setStartDate] = useState<string | undefined>()
  const [endDate, setEndDate] = useState<string | undefined>()
  const [annualInf, setAnnualInf] = useState<string | undefined>()
  const [annualInfLock, setAnnualInfLock] = useState<boolean>(false)
  const [archived, setArchived] = useState<boolean>(false)
  const [editOpen, setEditOpen] = useState<boolean>(false)
  const [saved, setSaved] = useState<boolean>(false)

  useEffect(() => {
    init(deduction)
  }, [deduction, projected])

  const init = (deduction: Deduction) => {
    setDeductionModel(deduction)
    setDescription(deduction.description)
    // const itemAmount = modelStore.getBudgetItemAmountDetail(deduction, model, snapshot)
    // setAmount(numberToMoneyFormat(itemAmount.amount, 0))
    // setAmountDate((dateToLocalFormat(itemAmount.amountDate)))
    setAmount(numberToMoneyFormat(deduction.amount, 0))
    setAmountDate(dateToLocalFormat(deduction.amountDate))
    setProjectedAmount(projected ? numberToMoneyFormat(projected, 0) : "$0")
    setOwner(model.getPerson(deduction.ownerId)?.nickname ?? Model.jointNickname)
    setAsset(model.getAssetById(deduction.assetId))
    setSchedule(deduction.schedule.getDescription(deduction.startDate))
    setStartDate(dateToLocalFormat(deduction.startDate))
    setEndDate(dateToLocalFormat(deduction.endDate))
    setAnnualInf(numberToPercentFormat(isNumber(deduction.annualInf) ? deduction.annualInf : model.getDefaultInflationRate("deduction"), 2))
    setAnnualInfLock(!isNumber(deduction.annualInf) || model.hasLock(deduction.id, "annualInf"))
    // TODO: receiptMonths, Days
    if (deduction.paycheckId) {
      const income = model.getIncomeById(deduction.paycheckId)
      if (income) {
        setLabel(income.description)
      }
    }
    setArchived(deduction.archived)
    setEditOpen(!deduction.createdAt)
  }

  const theme = useTheme()

  const styles = {
    heading: {
      color: theme.palette.primary.main,
      fontWeight: 'bold'
    },
    row: {
      height: 40,
      minHeight: 40,
      maxHeight: 40,
    },
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "1px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    }
  }

  const renderContent = () => {
    let assetError = false
    let assetDescription
    if (deductionModel.assetId && deductionModel.assetId !== "none") {
      if (asset) {
        if (asset.archived && !deductionModel.archived) {
          assetError = true
          assetDescription = `${asset.description} (Archived)`
        } else {
          assetDescription = asset.description
        }
      } else {
        assetError = true
        assetDescription = "Not found"
      }
    }

    return (
      <Grid container direction="column" spacing={0}>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Amount</FormLabel>
              <Typography variant="body2">
                {amount}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Amount Date</FormLabel>
              <Typography variant="body2">
                {amountDate}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={styles.row}>
          <FormLabel sx={styles.formLabel}>Frequency</FormLabel>
          <Typography variant="body2">
            {schedule}
          </Typography>
        </Grid>
          <Grid item xs={12} sx={styles.row}>
            <Grid container direction="row" spacing={0}>
              <Grid item xs={4}>
                <FormLabel sx={styles.formLabel}>Owner</FormLabel>
                <Typography variant="body2">
                  {owner}
                </Typography>
              </Grid>
              {(deductionModel.assetId && deductionModel.assetId !== "none") &&
                <Grid item xs={8}>
                  <FormLabel sx={styles.formLabel}>Contribution Account</FormLabel>
                  <Typography variant="body2" color={assetError ? "error" : undefined}>
                    {assetDescription}
                  </Typography>
                </Grid>
              }
            </Grid>
          </Grid>
        <Grid item xs={12} sx={styles.row}>
          <Grid container direction="row" spacing={0}>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Start Date</FormLabel>
              <Typography variant="body2">
                {startDate}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>End Date</FormLabel>
              <Typography variant="body2" color={deduction.end ? "inherit" : "darkgrey"}>
                {endDate}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <FormLabel sx={styles.formLabel}>Annual Inflation %</FormLabel>
              <Typography variant="body2" color={annualInfLock ? "text.secondary" : "inherit"}>
                {annualInf}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  return (
    <React.Fragment>
      <ModelCard2
        label={label}
        icon={<DeductionExpenseIcon/>}
        title={description}
        value={projectedAmount}
        valueColor="#881E10"
        archived={archived}
        expanded={expanded}
        onChange={onChange}
        editButtonId={`edit-btn-${deductionModel.id}`}
        onEdit={(event: any) => {
          setEditOpen(!editOpen)
        }}
        dragHandleProps={dragHandleProps}
      >
        {renderContent()}
      </ModelCard2>
      {editOpen &&
        <DeductionEditDialog deduction={deductionModel} model={model} snapshot={snapshot}
                             open={editOpen}
                             onSave={(update: Deduction) => {
                               init(update)
                               setEditOpen(false)
                               if (onSave) {
                                 onSave(update)
                                 setSaved(true)
                               }
                             }}
                             onDelete={(deleted: Deduction) => {
                               setEditOpen(false)
                               if (onDelete) {
                                 onDelete(deleted)
                               }
                             }}
                             onArchive={(deduction: Deduction) => {
                               setEditOpen(false)
                               if (onArchive) {
                                 onArchive(deduction)
                               }
                             }}
                             onClose={() => {
                               setEditOpen(false)
                               if (!deductionModel.createdAt && onDelete) {
                                 onDelete(deductionModel)
                               }
                             }}/>
      }
    </React.Fragment>
  )
}

export default DeductionCard